// Classification des seances par type de travail (fractionne court, moyen,
// long, seuil, tempo, pyramide, combinee, endurance), deduite du detail
// calcule par le moteur de seance (seanceEngine.ts). Le type peut etre
// corrige a la main (typeForce), cf Construction.tsx.
import { analyserSeanceDetail, type LigneDetail } from './seanceEngine.ts';
import { type ZoneRow } from './allureTable.ts';

export type CodeType =
  | 'COURT'
  | 'MOYEN'
  | 'LONG'
  | 'SEUIL'
  | 'TEMPO'
  | 'PYRAMIDE'
  | 'COMBINEE'
  | 'ENDURANCE';

export type TypeSeance = {
  code: CodeType;
  libelle: string;
  abrege: string;
  description: string;
};

export const TYPES_SEANCE: TypeSeance[] = [
  { code: 'COURT', libelle: 'Fractionné court', abrege: 'Court', description: 'Répétitions de 15" à 1\'30 (≈ 100–400 m), VMA / 3 km, récupération ≥ effort. Objectif : VMA, puissance.' },
  { code: 'MOYEN', libelle: 'Fractionné moyen', abrege: 'Moyen', description: 'Répétitions de 1\'30 à 4\' (≈ 400–1000 m), 5 km / 3 km, récupération 50–100 %. Objectif : tolérance à l\'intensité.' },
  { code: 'LONG', libelle: 'Fractionné long', abrege: 'Long', description: 'Répétitions de 4\' à 8\' (≈ 1–2 km), 10 km / semi, récupération courte. Objectif : seuil haut.' },
  { code: 'SEUIL', libelle: 'Seuil / tempo fractionné', abrege: 'Seuil', description: 'Répétitions de 8\' à 15\', semi / marathon, récupération courte. Objectif : seuil, endurance soutenue.' },
  { code: 'TEMPO', libelle: 'Tempo continu', abrege: 'Tempo', description: 'Bloc continu de plus de 15\', marathon / semi. Objectif : allure spécifique.' },
  { code: 'PYRAMIDE', libelle: 'Pyramide / progressive', abrege: 'Pyramide', description: 'Durées ou distances qui varient dans la séance (ex : 1\'-2\'-3\'-4\', 1600-1200-800-400).' },
  { code: 'COMBINEE', libelle: 'Combinée', abrege: 'Combinée', description: 'Enchaînement de types différents (ex : tempo marathon puis VMA courte).' },
  { code: 'ENDURANCE', libelle: 'Endurance', abrege: 'Endurance', description: 'Footing continu en endurance (Récup / Easy / Steady).' },
];

export function typeSeance(code?: string | null): TypeSeance | undefined {
  return TYPES_SEANCE.find((t) => t.code === code);
}

// Seuils (secondes d'effort par repetition) entre types de fractionne.
const SEUILS = { court: 90, moyen: 240, long: 480, seuil: 900 };
// Part minimale (temps d'effort) du type dominant pour qu'il donne son nom
// a la seance ; en dessous, la seance est "Combinee".
const PART_DOMINANTE = 0.75;

function typeSelonDuree(effortS: number): CodeType {
  if (effortS < SEUILS.court) return 'COURT';
  if (effortS < SEUILS.moyen) return 'MOYEN';
  if (effortS < SEUILS.long) return 'LONG';
  if (effortS < SEUILS.seuil) return 'SEUIL';
  return 'TEMPO';
}

/**
 * Type d'une seance a partir de son detail (une ligne par bloc). Les blocs
 * de recuperation sont ignores, sauf si la seance n'a que ca (endurance) ;
 * les blocs Easy et Steady comptent comme endurance.
 * @return null si la seance est vide ou invalide
 */
export function classerSeance(detail: LigneDetail[], table: ZoneRow[]): CodeType | null {
  const codeZone = (nom?: string) => table.find((z) => z.nom === nom)?.code;
  // Seance uniquement en RECUP (ex : 40'@RECUP) : footing tres lent.
  if (detail.length > 0 && detail.every((l) => !l.erreur && codeZone(l.nomZone) === 'RECUP')) return 'ENDURANCE';
  const lignes = detail.filter((l) => !l.erreur && l.nomZone && codeZone(l.nomZone) !== 'RECUP');
  if (lignes.length === 0) return null;

  const efforts = lignes.filter((l) => !['EASY', 'STEADY'].includes(codeZone(l.nomZone) ?? ''));
  if (efforts.length === 0) return 'ENDURANCE';

  const blocs = efforts.map((l) => ({
    duree: l.effortTimeS ?? 0,
    reps: l.nbReps ?? 1,
    type: typeSelonDuree(l.effortTimeS ?? 0),
  }));

  // Pyramide / progressive : au moins 3 blocs repetes le meme nombre de
  // fois, aux durees toutes differentes, couvrant au moins 2 types.
  const memesReps = blocs.every((b) => b.reps === blocs[0].reps);
  const durees = new Set(blocs.map((b) => Math.round(b.duree / 5)));
  const types = new Set(blocs.map((b) => b.type));
  if (blocs.length >= 3 && memesReps && durees.size >= 3 && types.size >= 2) return 'PYRAMIDE';

  // Type dominant (temps d'effort cumule), sinon combinee.
  const parType = new Map<CodeType, number>();
  blocs.forEach((b) => parType.set(b.type, (parType.get(b.type) ?? 0) + b.duree * b.reps));
  const total = Array.from(parType.values()).reduce((a, b) => a + b, 0);
  let dominant: CodeType = blocs[0].type;
  parType.forEach((v, t) => {
    if (v > (parType.get(dominant) ?? 0)) dominant = t;
  });
  return total > 0 && (parType.get(dominant) ?? 0) / total >= PART_DOMINANTE ? dominant : 'COMBINEE';
}

/**
 * Type d'une seance a partir de sa notation (seances enregistrees avant la
 * classification, ou recalcul apres retour au mode automatique).
 */
export function typeAutomatique(notation: string, vma: number | null | undefined, table: ZoneRow[]): CodeType | null {
  return classerSeance(analyserSeanceDetail(notation, vma && vma > 0 ? vma : 16.5, table), table);
}

/** Type a afficher : celui enregistre, sinon calcule depuis la notation. */
export function typeEffectif(
  seance: { typeSeance?: string | null; notation: string; vma?: number | null },
  table: ZoneRow[],
): CodeType | null {
  return (typeSeance(seance.typeSeance)?.code) ?? typeAutomatique(seance.notation, seance.vma, table);
}
